/**
 * Filtro Empresa da tela Meus Arquivos (admin): regras puras, sem React nem RTK Query. Quem chama
 * passa a resposta de `GET produtos/file-viewer/empresas/` e recebe as opções no formato que o
 * `OptionsPopover` do Flux já mostra (`MyStudiesFilterOption`).
 *
 * - "Todas as empresas" fixo no topo (a visão da própria empresa, sem `empresa` na URL), depois
 *   uma opção por empresa, por nome, com o apelido embaixo (se difere do nome) e "N pastas".
 * - Ids repetidos na resposta: fica só o primeiro, porque o teclado e os ids do DOM usam o id
 *   como chave; id não positivo sai, porque nunca iria na URL.
 */

import { MyStudiesFilterOption } from 'helper/flux/myStudiesFilterOptions';
import {
  CompanyScope,
  FileViewerCompanies,
  FileViewerCompanyOption,
  RawFileViewerCompanies,
} from 'helper/products/fileViewer';

/** Primeiro item fixo da lista e texto do botão sem empresa escolhida. */
export const ALL_COMPANIES_LABEL = 'Todas as empresas';

/** Opção do filtro Empresa, com o escopo que vai para o `useFileList`. */
export interface CompanyScopeOption extends MyStudiesFilterOption {
  value: CompanyScope;
}

const formatCount = (count: number): string => count.toLocaleString('pt-BR');

const plural = (count: number, singular: string, many: string): string => (
  `${formatCount(count)} ${count === 1 ? singular : many}`
);

const pluralCompanies = (count: number): string => plural(count, 'empresa', 'empresas');
const pluralFolders = (count: number): string => plural(count, 'pasta', 'pastas');

/** Ordem de nomes em pt-BR: "alfa" < "ômega" < "Zeta", sem diferenciar acento nem caixa. */
const byName = new Intl.Collator('pt-BR', { sensitivity: 'base' });

/**
 * Id de empresa que pode ir na URL (`?empresa=<id>`): inteiro positivo. O backend responde 422 a
 * qualquer outra coisa, então quem monta a URL e quem escolhe a entrada de cache usam esta regra.
 */
export const isCompanyId = (id: unknown): id is number => (
  Number.isInteger(id) && (id as number) > 0
);

const asCount = (value: unknown): number => (
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.trunc(value) : 0
);

const asText = (value: unknown): string => (typeof value === 'string' ? value : '');

/**
 * Converte a resposta de `/empresas/` para o domínio. Id não positivo sai; id repetido fica uma
 * vez (o primeiro); a lista sai por nome em pt-BR (ordenação estável: nomes iguais mantêm a
 * ordem do servidor). O total fica como o backend mandou.
 *
 * @param raw - `data` da resposta (não-admin: `itens: []` e total 0).
 * @returns Total e opções ordenadas.
 */
export function toFileViewerCompanies(raw: RawFileViewerCompanies): FileViewerCompanies {
  const seen = new Set<number>();
  const itens = Array.isArray(raw?.itens) ? raw.itens : [];
  const options = itens
    .filter((item) => {
      if (!isCompanyId(item?.id) || seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    })
    .map((item): FileViewerCompanyOption => ({
      id: item.id,
      name: asText(item.nome),
      nickname: asText(item.apelido),
      folderCount: asCount(item.total_pastas),
    }))
    .sort((a, b) => byName.compare(a.name, b.name));

  return { totalCompanies: asCount(raw?.total_empresas), options };
}

/**
 * Lista do filtro Empresa: "Todas as empresas" (com "N empresas", sem contagem) e, com a
 * resposta, uma opção por empresa na ordem dela, com o apelido embaixo quando difere do nome e
 * "N pastas" à direita. A busca olha o nome e o apelido.
 *
 * Sem a resposta (carregando ou com erro), "Todas as empresas" continua escolhível, sem o
 * "N empresas".
 *
 * @param summary - Resposta de `/empresas/`, ou `undefined` enquanto carrega ou com erro.
 * @returns As opções na ordem da lista.
 */
export function buildCompanyOptions(
  summary: FileViewerCompanies | undefined,
): CompanyScopeOption[] {
  const allDetail = summary ? pluralCompanies(summary.totalCompanies) : '';
  const companies = (summary?.options ?? []).map((option): CompanyScopeOption => {
    const detail = option.nickname !== option.name ? option.nickname : '';
    return {
      key: String(option.id),
      value: { companyId: option.id },
      label: option.name,
      detail,
      count: pluralFolders(option.folderCount),
      emphasized: false,
      searchText: detail ? [option.name, detail] : [option.name],
    };
  });

  return [
    {
      key: 'todas',
      value: 'todas',
      label: ALL_COMPANIES_LABEL,
      detail: allDetail,
      count: '',
      emphasized: true,
      searchText: [ALL_COMPANIES_LABEL, allDetail],
    },
    ...companies,
  ];
}

/**
 * Texto do botão Empresa.
 *
 * @param scope - Escopo escolhido.
 * @param summary - Resposta de `/empresas/`, se já chegou.
 * @returns "Todas as empresas", o nome da empresa ou, sem ele, "#<id>".
 */
export function companyLabel(
  scope: CompanyScope,
  summary: FileViewerCompanies | undefined,
): string {
  if (scope === 'todas') return ALL_COMPANIES_LABEL;
  const name = summary?.options.find((option) => option.id === scope.companyId)?.name;
  return name || `#${scope.companyId}`;
}

/**
 * Placeholder da busca de empresas.
 *
 * @param summary - Resposta de `/empresas/`, se já chegou.
 * @returns "Buscar entre 59 empresas"; "Buscar empresa" enquanto não há total.
 */
export function companySearchPlaceholder(summary: FileViewerCompanies | undefined): string {
  return summary ? `Buscar entre ${pluralCompanies(summary.totalCompanies)}` : 'Buscar empresa';
}
